import React, { ReactNode, useEffect } from 'react';
import { formFieldType } from '@helpers/consts';
import { RadioButton } from '@components/RadioButton';
import { TextArea } from '@components/TextArea';
import { Toggle } from './components/Toggle';
import { Input } from '@components/Input';
import {
  Path,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { FormValues, FileFormField, FormField, Options } from './Form.types';
import { FileUpload } from '@components/FileUpload';
import { Checkbox } from '@components/Checkbox';
import { FormSelect } from './components/FormSelect';
import { TextBoxList } from './components/TextBoxList';
import { SerialLink } from './components/SerialLink';
import { useFetchEnquiryApplicationListQuery } from '@modules/EnquiryForm/EnquiryForm.service';
import { QuoteItem } from '@components/QuoteItem';
import { PhoneNumber, countryCodes } from '@components/PhoneNumber';

const useApplicationFieldState = ({
  watchIndustriesSelector,
  onFetchApplicationData,
}: {
  watchIndustriesSelector: string;
  onFetchApplicationData: () => void;
}) => {
  const { data: applicationDataResponse } = useFetchEnquiryApplicationListQuery(
    +watchIndustriesSelector,
    { skip: !watchIndustriesSelector || watchIndustriesSelector === 'select' }
  );
  let applicationData = JSON.parse(applicationDataResponse?.ApplicationJsonList || '[]') as Options;

  if (watchIndustriesSelector === 'select') {
    applicationData = [] as Options;
  }
  useEffect(() => {
    onFetchApplicationData();
  }, [applicationDataResponse?.ApplicationJsonList]);

  return applicationData;
};

export function useFormLoader(
  register: UseFormRegister<FormValues>,
  watch: UseFormWatch<FormValues>,
  setFormValue: UseFormSetValue<FormValues>,
  trigger: UseFormTrigger<FormValues>,
  hasToggleField: boolean,
  madeFirstSubmit: boolean,
  dropdownToDisable?: string
): (formField: FormField | FileFormField, index: number) => ReactNode {
  const watchToggle = watch('Toggle', false);
  const watchCountry = watch('Country');
  const selectedProductNames = watch('selectedProductNames');

  const applicationData = useApplicationFieldState({
    watchIndustriesSelector: watch('Industry'),
    onFetchApplicationData: () => {
      setFormValue('Application', 'select');
    },
  });

  return (formField: FormField | FileFormField, index: number) => {
    const dataType = formField.DataType?.toLowerCase() || '';
    const name = formField.Name as Path<FormValues>;
    const productIsRemoved = !selectedProductNames?.includes(formField.Name);

    switch (dataType) {
      case formFieldType.quoteitem: {
        if (!selectedProductNames?.length || productIsRemoved) {
          return null;
        }
        return (
          <QuoteItem
            itemImage={formField.ImageSrc}
            itemName={formField.Name}
            itemDescription={formField.ProductDescription}
            buttonType='delete'
            className='form-product has-border'
            onButtonClick={() => {
              setFormValue(
                'selectedProductNames',
                selectedProductNames.filter((name) => name !== formField.Name)
              );
            }}
          />
        );
      }
      case formFieldType.textbox:
      case formFieldType.email:
        return (
          <div key={index} className='c-contact-us-field-label'>
            <Input
              label={formField.Label}
              type={formFieldType.textbox ? 'text' : 'email'}
              className='c-contact-us-field-label'
              placeholder={formField.Placeholder}
              required={formField.IsMandatory}
              {...register(name, {
                required: { value: formField.IsMandatory, message: formField.MandatoryMessage },
                pattern: {
                  value: new RegExp(formField.ValidationRegex?.replace('\\\\', '\\'), 'gu'),
                  message: formField.ValidationMessage,
                },
              })}
            />
          </div>
        );
      case formFieldType.phone:
        return (
          <div key={index} className='c-contact-us-field-label'>
            <PhoneNumber
              name={name}
              label={formField.Label}
              className='c-contact-us-field-label'
              placeholder={formField.Placeholder}
              country={countryCodes[watchCountry.toString()]}
              required={formField.IsMandatory}
              {...register(name, {
                required: { value: formField.IsMandatory, message: formField.MandatoryMessage },
              })}
              onChange={(value) => {
                setFormValue(name, value);
              }}
            />
          </div>
        );
      case formFieldType.textarea:
        return (
          <div key={index} className='c-contact-us-field-label'>
            <TextArea
              label={formField.Label}
              className='c-contact-us-field-label c-contact-us-textarea'
              placeholder={formField.Placeholder}
              required={formField.IsMandatory}
              {...register(name, {
                required: { value: formField.IsMandatory, message: formField.MandatoryMessage },
                pattern: {
                  value: new RegExp(formField.ValidationRegex?.replace('\\\\', '\\'), 'gu'),
                  message: formField.ValidationMessage,
                },
              })}
            />
          </div>
        );

      case formFieldType.radio:
        const radioName = name || 'ServiceType';

        return (
          <div key={index} className='c-contact-radio-main'>
            {formField.Options.map((opt, index) => (
              <div className='c-contact-radio' key={index}>
                <RadioButton
                  title={opt.Text}
                  id={opt.Value}
                  {...register(radioName, {
                    required: { value: formField.IsMandatory, message: formField.MandatoryMessage },
                  })}
                  value={opt.Text}
                />
              </div>
            ))}
          </div>
        );
      case formFieldType.toggle: {
        const sparePartText = formField.Options.find((item) => item.Value === 'Spareparts').Text;
        const newQuoteText = formField.Options.find((item) => item.Value === 'Newquote').Text;

        return (
          <Toggle
            id='service_type'
            label={formField.Label}
            onText={sparePartText}
            offText={newQuoteText}
            {...register(name)}
          />
        );
      }
      case formFieldType.textboxlist: {
        if (hasToggleField && !watchToggle) {
          return null;
        }

        register(name, {
          validate: (value) => {
            if (!formField.IsMandatory) {
              return true;
            }

            const val = value as string[];
            // if array of serial numbers is empty, return error message
            return !val.join('') ? formField.MandatoryMessage : true;
          },
        });

        const onBlur = () => {
          // trigger validation on blur if form was submitted at least once
          if (madeFirstSubmit) {
            trigger(name);
          }
        };

        return (
          <div className='c-contact-us-serial'>
            <label className='c-contact-us-field-label'>{formField.Label}</label>
            <br />
            <SerialLink linkLabel={formField.LinkLabel} />
            <TextBoxList
              name={name}
              placeholder={formField.Placeholder}
              addInputBtnText={formField.LinkButtonLabel}
              onChange={(value) => {
                setFormValue(name, value);
              }}
              onBlur={onBlur}
            />
          </div>
        );
      }
      case formFieldType.file:
        const field = formField as FileFormField;
        register(name);
        return (
          <FileUpload
            name={name}
            key={index}
            label={field.Label}
            required={field.IsMandatory}
            extensions={field.FileExtensions || 'jpg,jpeg,png,xlsx,pdf'}
            btnLabel={field.FileUploadButtonLabel}
            maxSize={field.FileSize}
            validationErrorMsg={field.ValidationMessage}
            sizeValidationMsg={field.FileSizeValidationMessage}
            onFilesUpdate={(files) => {
              setFormValue(name, files);
            }}
          />
        );
      case formFieldType.checkbox:
        return (
          <div key={index} className='c-form-checkbox'>
            <Checkbox
              label=''
              id={name}
              {...register(name, {
                validate: (value) => {
                  if (formField.IsMandatory && !value) {
                    return formField.MandatoryMessage;
                  }
                  return true;
                },
                onChange: () => {
                  if (name === 'GDPR') {
                    trigger(name);
                  }
                },
              })}
            />
            <div
              className='c-form-checkbox-label'
              dangerouslySetInnerHTML={{ __html: formField.Label }}
            />
          </div>
        );
      case formFieldType.regionselection:
      case formFieldType.industryselection:
      case formFieldType.productselection: {
        const options =
          formField.IndustrySelectOptions ||
          formField.RegionSelectOptions ||
          formField.ProductSelectOptions;

        return (
          <FormSelect
            hasGroups
            key={index}
            label={formField.Label}
            selectText={formField.SelectLabel}
            options={options}
            required={formField.IsMandatory}
            disabled={!options.length || dropdownToDisable === formField.DataType}
            {...register(name, {
              validate: (value) => {
                const isValid = formField.IsMandatory ? value !== 'select' : true;
                return isValid || formField.MandatoryMessage;
              },
            })}
          />
        );
      }
      case formFieldType.applicationselection:
        if (!applicationData.length) {
          return null;
        }
        return (
          <FormSelect
            key={index}
            label={formField.Label}
            selectText={formField.SelectLabel}
            hasGroups={false}
            options={applicationData}
            required={formField.IsMandatory}
            {...register(name, {
              validate: (value) => {
                const isValid = formField.IsMandatory ? value !== 'select' : true;
                return isValid || formField.MandatoryMessage;
              },
            })}
          />
        );
    }
  };
}
